import { useMemo, useState } from 'react'

import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'

import { Form, fieldString } from '@subaio/components/src/HookForm'
import { isNonNullable, toInlineSafe } from '@subaio/utils'
import { object } from '@subaio/validation'

import { CodeSelect } from 'components/FormFields'

import { useApiContext } from '../context'
import { useShowDeprecated } from '../Deprecation/useShowDeprecated'
import { getPropertyValue } from '../getDeepReferences'
import { Container } from './Container'
import { Content } from './Content'
import { Header } from './Header'
import { SyntaxHighlighter } from './SyntaxHighlighter'

const schema = object({
	example: fieldString(),
	contentType: fieldString(),
})

type Props = {
	title: string
	data: OpenApi3.ResponseObject | OpenApi3.RequestBodyObject | undefined
	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	selectedContentType: string

	stickyOffset?: number

	setSelectedContentType: (selectedContentType: string) => void
}

export const Example = ({
	title,
	definitions,
	data,
	selectedContentType: initialSelectedContentType,
	setSelectedContentType: setDefaultSelectedContentType,
	stickyOffset,
}: Props) => {
	const contentTypes = data?.content ? Object.keys(data.content) : []
	const [selectedContentType, setSelectedContentType] = useState(initialSelectedContentType ?? contentTypes[0])
	const { getRefValue } = useApiContext()

	const selectedContent = data?.content?.[selectedContentType]

	const showDeprecated = useShowDeprecated()

	const examples = useMemo(() => {
		const defaultExample = selectedContent?.example
			? {
					id: 'default',
					title: `${title} example`,
					data: selectedContent?.example,
				}
			: null

		const generatedExample = {
			id: 'generated',
			title: defaultExample ? `Generated ${toInlineSafe(title)} example` : `${title} example`,
			data:
				selectedContent?.schema && '$ref' in selectedContent.schema
					? getPropertyValue({
							definitions,
							hideDeprecatedProperties: !showDeprecated,
							schema: getRefValue(selectedContent.schema.$ref)!,
						})
					: null,
		}

		const specExamples = selectedContent?.examples
			? Object.entries(selectedContent.examples).map(([key, example]) => ({
					id: key,
					title: example.summary ?? key,
					data:
						'$ref' in example
							? (selectedContent.example ??
								getPropertyValue({
									definitions,
									hideDeprecatedProperties: !showDeprecated,
									schema: getRefValue(example.$ref)!,
								}))
							: example.value,
				}))
			: []

		return [defaultExample, ...specExamples, generatedExample].filter(isNonNullable)
	}, [selectedContent, title, definitions, showDeprecated, getRefValue])

	const [selectedExampleId, setSelectedExampleId] = useState<string>(examples[0].id)

	return (
		<Container stickyOffset={stickyOffset}>
			<Form
				schema={schema}
				initialValues={{
					example: selectedExampleId,
					contentType: selectedContentType,
				}}
				render={({ example, contentType }) => (
					<Header>
						<CodeSelect
							label=''
							{...example}
							options={examples.map(({ id, title }) => ({
								label: title,
								value: id,
							}))}
							isDisabled={examples.length <= 1}
							isClearable={false}
						/>
						<CodeSelect
							label=''
							{...contentType}
							options={
								contentTypes.length > 0
									? contentTypes.map((value) => ({ label: value, value }))
									: [{ label: 'No content type', value: 'null' }]
							}
							isDisabled={contentTypes.length <= 1}
							isClearable={false}
						/>
					</Header>
				)}
				onSubmit={() => undefined}
				onChange={(fields) => {
					if (fields.contentType !== selectedContentType) {
						if (setDefaultSelectedContentType) setDefaultSelectedContentType(fields.contentType!)
						setSelectedContentType(fields.contentType!)
					}
					if (fields.example !== selectedExampleId) {
						setSelectedExampleId(fields.example!)
					}
				}}
			/>
			{(() => {
				const selectedExample = examples.find(({ id }) => id === selectedExampleId)
				if (selectedExample?.data) {
					return (
						<SyntaxHighlighter language='json' fontSize={12} limitHeight={false}>
							{JSON.stringify(selectedExample.data, null, 2)}
						</SyntaxHighlighter>
					)
				}
				return <Content>There is no {toInlineSafe(title)}</Content>
			})()}
		</Container>
	)
}
